import { CSSProperties, ReactNode, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useIntl } from 'react-intl';
import type { WorkoutDraft } from '../../stores/useDraftWorkoutsStore';

interface Props {
  planName: string;
  onPlanNameChange: (name: string) => void;
  onBack: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  isViewOnlyMode: boolean;
  onAddGroup: () => void;
  onAddExercise: () => void;
  onImportAI: () => void;
  onSaveDraft: () => void;
  drafts: WorkoutDraft[];
  currentDraftId: string | null;
  onLoadDraft: (id: string) => void;
  onDeleteDraft: (id: string) => void;
  onNewBlank: () => void;
  totalGroups: number;
  totalExercises: number;
}

/**
 * Header del create-plan editor — Variant B del design handoff.
 *
 * Dos filas:
 *   1. Identity: back · nombre del plan · cancelar · guardar plan
 *   2. Toolbar segmentado: CREAR (Grupo / Ejercicio / Importar IA) · BORRADORES
 *      (Guardar / Cargar) · spacer · pill de stats
 *
 * View-only mode oculta la fila 2 + botones de acción de fila 1.
 *
 * El draft loader es un OverlayPanel disparado por el botón "Cargar"; la lista
 * dentro replica el patrón del componente `DraftSelector` original pero inline.
 */
export function CreatePlanHeader({
  planName,
  onPlanNameChange,
  onBack,
  onCancel,
  onSave,
  isSaving,
  isViewOnlyMode,
  onAddGroup,
  onAddExercise,
  onImportAI,
  onSaveDraft,
  drafts,
  currentDraftId,
  onLoadDraft,
  onDeleteDraft,
  onNewBlank,
  totalGroups,
  totalExercises
}: Props) {
  const intl = useIntl();
  const draftPanelRef = useRef<OverlayPanel>(null);
  const sortedDrafts = [...drafts].sort((a, b) => b.updatedAt - a.updatedAt);

  return (
    <div style={{ padding: '0.75rem', maxWidth: '900px', margin: '0 auto' }}>
      <div
        style={{
          background: 'var(--ios-card-bg)',
          border: '1px solid var(--ios-card-border)',
          borderRadius: 'var(--ios-radius-lg)',
          boxShadow: 'var(--ios-card-shadow)',
          overflow: 'hidden',
          marginBottom: '0.75rem'
        }}
      >
        {/* Row 1 — identity */}
        <div
          style={{
            padding: '0.75rem 0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            borderBottom: '1px solid var(--ios-divider)',
            flexWrap: 'wrap'
          }}
        >
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-rounded p-button-sm"
            onClick={onBack}
            tooltip="Volver"
            style={{
              color: 'var(--ios-text-secondary)',
              width: '2rem',
              height: '2rem',
              flexShrink: 0
            }}
          />
          <div style={{ flex: 1, minWidth: '12rem' }}>
            <InputText
              id="plan-name"
              value={planName}
              onChange={(e) => onPlanNameChange(e.target.value)}
              className="w-full"
              maxLength={255}
              disabled={isViewOnlyMode}
              placeholder="Nombre del plan"
              style={{
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 'var(--ios-radius-md)',
                border: '1px solid var(--ios-divider)'
              }}
            />
          </div>
          {!isViewOnlyMode && (
            <>
              <Button
                label="Cancelar"
                className="p-button-text p-button-sm"
                onClick={onCancel}
                style={{ color: 'var(--ios-text-secondary)', fontWeight: 600 }}
              />
              <Button
                label="Guardar plan"
                icon="pi pi-check"
                onClick={onSave}
                loading={isSaving}
                disabled={isSaving}
                className="p-button-sm"
                style={{
                  background: '#a3e635',
                  color: '#1f2a05',
                  border: 'none',
                  borderRadius: 'var(--ios-radius-md)',
                  fontWeight: 700,
                  fontSize: '0.85rem'
                }}
              />
            </>
          )}
        </div>

        {/* Row 2 — toolbar segmentado */}
        {!isViewOnlyMode && (
          <div
            style={{
              padding: '0.5rem 0.625rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.875rem',
              background: 'var(--ios-surface-subtle)',
              flexWrap: 'wrap'
            }}
          >
            <Segment label="Crear">
              <SegBtn icon="pi pi-plus" label="Grupo" accent onClick={onAddGroup} />
              <SegBtn
                icon="pi pi-plus"
                label={intl.formatMessage({ id: 'coach.quickCreateExercise.headerButton' })}
                onClick={onAddExercise}
              />
              <SegBtn
                icon="pi pi-sparkles"
                label={intl.formatMessage({ id: 'coach.importWorkoutAI.entryButton' })}
                onClick={onImportAI}
              />
            </Segment>

            <Divider />

            <Segment label="Borradores">
              <SegBtn
                icon="pi pi-bookmark"
                label={intl.formatMessage({ id: 'coach.drafts.saveButton' })}
                onClick={onSaveDraft}
              />
              <SegBtn
                icon="pi pi-folder"
                label={intl.formatMessage({ id: 'coach.drafts.button' }, { count: drafts.length, max: 3 })}
                iconRight="pi pi-chevron-down"
                disabled={drafts.length === 0}
                onClick={(e) => draftPanelRef.current?.toggle(e)}
              />
            </Segment>

            <div style={{ flex: 1 }} />

            <StatsPill totalGroups={totalGroups} totalExercises={totalExercises} />
          </div>
        )}
      </div>

      <OverlayPanel ref={draftPanelRef} dismissable style={{ minWidth: '20rem', maxWidth: '94vw' }}>
        <div className="flex flex-column gap-2">
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              color: 'var(--ios-text-secondary)',
              padding: '0 0.25rem'
            }}
          >
            {intl.formatMessage({ id: 'coach.drafts.heading' })}
          </div>
          {sortedDrafts.length === 0 ? (
            <div style={{ padding: '0.75rem', color: 'var(--ios-text-tertiary)', fontSize: '0.85rem' }}>
              {intl.formatMessage({ id: 'coach.drafts.heading' })} — 0
            </div>
          ) : (
            sortedDrafts.map((d) => {
              const isActive = d.id === currentDraftId;
              return (
                <div
                  key={d.id}
                  className="flex align-items-center gap-2"
                  style={{
                    background: isActive ? 'rgba(99,102,241,0.08)' : 'var(--ios-surface-subtle)',
                    border: '1px solid var(--ios-divider)',
                    borderRadius: 'var(--ios-radius-md)',
                    padding: '0.5rem 0.6rem'
                  }}
                >
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '0.88rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={d.planName || intl.formatMessage({ id: 'coach.drafts.untitled' })}
                    >
                      {d.planName || intl.formatMessage({ id: 'coach.drafts.untitled' })}
                    </div>
                    <div
                      style={{
                        fontSize: '0.72rem',
                        color: 'var(--ios-text-tertiary)',
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center'
                      }}
                    >
                      <span>
                        {d.source === 'ai' ? (
                          <span style={{ color: '#6366f1', fontWeight: 600 }}>
                            <i className="pi pi-sparkles" style={{ fontSize: '0.65rem' }} /> IA
                          </span>
                        ) : (
                          <span>Manual</span>
                        )}
                      </span>
                      <span>
                        · {intl.formatMessage({ id: 'coach.drafts.groupsCount' }, { count: d.groups.length })}
                      </span>
                    </div>
                  </div>
                  <Button
                    icon="pi pi-arrow-right"
                    tooltip={intl.formatMessage({ id: 'coach.drafts.continue' })}
                    className="p-button-text p-button-sm p-button-rounded"
                    onClick={() => {
                      onLoadDraft(d.id);
                      draftPanelRef.current?.hide();
                    }}
                    disabled={isActive}
                    style={{ color: '#6366f1' }}
                  />
                  <Button
                    icon="pi pi-trash"
                    tooltip={intl.formatMessage({ id: 'coach.drafts.delete' })}
                    className="p-button-text p-button-sm p-button-rounded"
                    onClick={() => onDeleteDraft(d.id)}
                    style={{ color: '#ef4444' }}
                  />
                </div>
              );
            })
          )}
          <div style={{ borderTop: '1px solid var(--ios-divider)', paddingTop: '0.5rem' }}>
            <Button
              icon="pi pi-plus"
              label={intl.formatMessage({ id: 'coach.drafts.newBlank' })}
              className="p-button-text p-button-sm w-full"
              onClick={() => {
                onNewBlank();
                draftPanelRef.current?.hide();
              }}
              style={{ justifyContent: 'flex-start', color: 'var(--ios-text-secondary)' }}
            />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
}

// ─────────────────────────────────────────────
// Sub-átomos: Segment, SegBtn, Divider, StatsPill
// (todos inline — no merece archivo aparte)
// ─────────────────────────────────────────────

interface SegmentProps {
  label: string;
  children: ReactNode;
}
function Segment({ label, children }: SegmentProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <span
        style={{
          fontSize: '0.6rem',
          fontWeight: 700,
          color: 'var(--ios-text-tertiary)',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          marginRight: '0.125rem',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          padding: '2px',
          borderRadius: '9px',
          background: 'var(--ios-surface-muted)',
          border: '1px solid var(--ios-divider)'
        }}
      >
        {children}
      </div>
    </div>
  );
}

interface SegBtnProps {
  icon?: string;
  label: string;
  iconRight?: string;
  accent?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
function SegBtn({ icon, label, iconRight, accent, disabled, onClick }: SegBtnProps) {
  const color = disabled ? 'var(--ios-text-tertiary)' : accent ? '#65a30d' : 'var(--ios-text)';
  const iconColor = disabled ? 'var(--ios-text-tertiary)' : accent ? '#65a30d' : 'var(--ios-text-secondary)';
  const style: CSSProperties = {
    height: '1.75rem',
    padding: '0 0.5625rem',
    borderRadius: '7px',
    background: 'transparent',
    color,
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
    opacity: disabled ? 0.55 : 1,
    transition: 'background 0.15s ease'
  };
  return (
    <button
      type="button"
      style={style}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {icon && <i className={icon} style={{ fontSize: '0.7rem', color: iconColor }} />}
      {label}
      {iconRight && <i className={iconRight} style={{ fontSize: '0.6rem', color: 'var(--ios-text-tertiary)' }} />}
    </button>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: '1px',
        height: '1.375rem',
        background: 'var(--ios-divider)'
      }}
    />
  );
}

interface StatsPillProps {
  totalGroups: number;
  totalExercises: number;
}
function StatsPill({ totalGroups, totalExercises }: StatsPillProps) {
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 999,
        background: 'transparent',
        color: 'var(--ios-text-secondary)',
        border: '1px solid var(--ios-divider)',
        fontSize: '0.72rem',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap'
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: '#a3e635'
        }}
      />
      {totalGroups} grupos · {totalExercises} ejercicios
    </span>
  );
}
